import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "./useFamily";
import { getDateString, getDayRange } from "@/lib/family-date";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-log";

/**
 * Classifies a task into a category using AI. This runs independently of AI coaching —
 * even when coaching is disabled by the parent, classification still fires for every
 * new task that doesn't already have a category.
 */
function classifyTaskInBackground(taskId: string, title: string) {
  supabase.functions.invoke("classify-task", {
    body: { taskId, title },
  }).catch((err) => console.warn("Task classification failed:", err));
}

export function useTaskItems(childId: string | undefined, overrideFamilyId?: string | null, overrideDate?: string) {
  const { familyId: authFamilyId, timezone } = useFamily();
  const familyId = overrideFamilyId ?? authFamilyId;
  const queryClient = useQueryClient();
  const today = getDateString(new Date(), timezone);
  const activeDate = overrideDate ?? today;
  const isAllTasks = activeDate === "all";

  const queryKey = ["task-items", childId, activeDate];

  const { data: items = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!childId) return [];
      if (isAllTasks) {
        const { data, error } = await supabase
          .from("task_items" as any)
          .select("*")
          .eq("child_id", childId)
          .order("created_at", { ascending: false })
          .order("id", { ascending: false });
        if (error) throw error;
        return data as any[];
      }
      const { data, error } = await supabase
        .from("task_items" as any)
        .select("*")
        .eq("child_id", childId)
        .or(`date.eq.${activeDate},and(deadline_date.gte.${activeDate},date.lte.${activeDate},status.neq.done)`)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!childId,
  });

  useEffect(() => {
    if (!childId) return;
    const channel = supabase
      .channel(`task-items-${childId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "task_items", filter: `child_id=eq.${childId}` },
        () => {
          queryClient.invalidateQueries({ queryKey });
          queryClient.invalidateQueries({ queryKey: ["points-total", childId] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [childId, today, queryClient]);

  const todayTaskCount = isAllTasks ? items.filter((i: any) => i.date === today).length : items.length;
  const canAdd = (isAllTasks ? todayTaskCount < 3 : activeDate >= today && items.length < 3);

  const addItem = useMutation({
    mutationFn: async ({ title, durationSeconds = 600, category, description }: { title: string; durationSeconds?: number; category?: string; description?: string }) => {
      if (!childId || !familyId) throw new Error("Missing context");
      const insertDate = isAllTasks ? today : activeDate;
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("task_items" as any)
        .insert({
          child_id: childId,
          family_id: familyId,
          date: insertDate,
          title,
          status: "pending",
          duration_seconds: durationSeconds,
          completion_points: 1,
          created_by: user?.id ?? null,
          category: category || null,
          description: description || null,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      if (data?.id && !data?.category) classifyTaskInBackground(data.id, data.title);
      if (data?.id && childId && familyId) {
        logActivity({ familyId, childId, action: "created", entityType: "task_item", entityId: data.id });
      }
      queryClient.invalidateQueries({ queryKey });
      if (isAllTasks) queryClient.invalidateQueries({ queryKey: ["task-items", childId, today] });
    },
    onError: () => {
      if (todayTaskCount >= 3) {
        toast.error("Du har allerede 3 ting for i dag");
      } else {
        toast.error("Kunne ikke legge til");
      }
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const startItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from("task_items" as any)
        .update({ status: "active", started_at: new Date().toISOString() } as any)
        .eq("id", itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const completeItem = useMutation({
    mutationFn: async ({ itemId, improvementNote }: { itemId: string; improvementNote?: string }) => {
      if (!childId || !familyId) throw new Error("Missing context");
      const item = items.find((i: any) => i.id === itemId);
      const category = item?.category;

      const { error: updateErr } = await supabase
        .from("task_items" as any)
        .update({
          status: "done",
          done_at: new Date().toISOString(),
          improvement_note: improvementNote || null,
        } as any)
        .eq("id", itemId);
      if (updateErr) throw updateErr;

      // Award 1 point if category is set and not "Annet"
      if (category && category !== "Annet") {
        const { error: ptErr } = await supabase
          .from("point_transactions")
          .insert({
            child_id: childId,
            family_id: familyId,
            task_item_id: itemId,
            amount: 1,
            reason: "Fullført oppgave",
          } as any);
        if (ptErr) throw ptErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["points-total", childId] });
    },
  });

  const updateItem = useMutation({
    mutationFn: async ({ itemId, title, durationSeconds, completionPoints, date, description }: { itemId: string; title?: string; durationSeconds?: number; completionPoints?: number; date?: string; description?: string }) => {
      const oldItem = items.find((i: any) => i.id === itemId);
      const updates: Record<string, any> = {};
      if (title !== undefined) updates.title = title;
      if (durationSeconds !== undefined) updates.duration_seconds = durationSeconds;
      if (completionPoints !== undefined) updates.completion_points = completionPoints;
      if (date !== undefined) updates.date = date;
      if (description !== undefined) updates.description = description || null;
      const { error } = await supabase
        .from("task_items" as any)
        .update(updates)
        .eq("id", itemId);
      if (error) throw error;
      // Audit log
      if (oldItem && childId && familyId) {
        const changes: Record<string, { old: unknown; new: unknown }> = {};
        if (title !== undefined && oldItem.title !== title) changes.title = { old: oldItem.title, new: title };
        if (description !== undefined && oldItem.description !== description) changes.description = { old: oldItem.description, new: description };
        if (Object.keys(changes).length > 0) {
          logActivity({ familyId, childId, action: "updated", entityType: "task_item", entityId: itemId, changes });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error("Kunne ikke oppdatere");
    },
  });

  const deleteItem = useMutation({
    mutationFn: async (itemId: string) => {
      const oldItem = items.find((i: any) => i.id === itemId);
      const { error } = await supabase
        .from("task_items" as any)
        .delete()
        .eq("id", itemId);
      if (error) throw error;
      if (oldItem && childId && familyId) {
        logActivity({ familyId, childId, action: "deleted", entityType: "task_item", entityId: itemId, changes: { title: { old: oldItem.title, new: null } } });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error("Kunne ikke slette");
    },
  });

  const resetItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error: updateErr } = await supabase
        .from("task_items" as any)
        .update({ status: "pending", started_at: null } as any)
        .eq("id", itemId);
      if (updateErr) throw updateErr;

      // Clean up any legacy start points
      await (supabase.from("point_transactions") as any)
        .delete()
        .eq("task_item_id", itemId)
        .eq("reason", "Startet fokus");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["points-total", childId] });
    },
    onError: () => {
      toast.error("Kunne ikke nullstille oppgaven");
    },
  });

  const approveItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("task_items" as any)
        .update({ approved_by: user.id } as any)
        .eq("id", itemId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      toast.error("Kunne ikke godkjenne oppgaven");
    },
  });

  return { items, isLoading, canAdd, addItem, startItem, completeItem, updateItem, deleteItem, resetItem, approveItem, today, activeDate };
}

export function usePointsToday(childId: string | undefined) {
  return useQuery({
    queryKey: ["points-total", childId],
    queryFn: async () => {
      if (!childId) return 0;
      const { data, error } = await supabase
        .from("point_transactions")
        .select("amount")
        .eq("child_id", childId);
      if (error) throw error;
      return (data || []).reduce((sum, r) => sum + r.amount, 0);
    },
    enabled: !!childId,
  });
}
